import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

import { getTranslate } from '../../localization'
import {
  PaymentFollowUp,
  PaymentFollowUpAction
} from '../../types/paymentFollowUps'
import { settlementActionLabel, settlementOutcomeLabel } from './labels'
import {
  initialSettlement,
  projectionPending,
  scopedSettlement,
  SettlementForm,
  settlementOutcomes
} from './settlement'

interface Props {
  followUp: PaymentFollowUp
  value: SettlementForm
  onChange: (value: SettlementForm) => void
  disabled: boolean
  invalid: boolean
}

export default function SettlementFields({
  followUp,
  value,
  onChange,
  disabled,
  invalid
}: Props) {
  if (
    followUp.caseSchemaVersion !== 2 ||
    !followUp.revision ||
    projectionPending(followUp) ||
    settlementOutcomes(followUp, value.action || undefined).length === 0
  ) {
    return (
      <Typography id="settlement-validation-error" role="alert" color="error">
        {getTranslate('Refresh this case before recording settlement.')}
      </Typography>
    )
  }
  const update = (key: keyof SettlementForm, next: string | boolean) =>
    onChange({ ...value, [key]: next })
  const monetary = value.outcome !== 'ReviewedNoFurtherAction'
  const wallet = value.outcome === 'AcceptedWalletSettlement'
  const credit = value.outcome === 'WalletCreditConfirmed'
  const unknown =
    scopedSettlement(followUp) &&
    followUp.outstandingByAction?.find((row) => row.action === value.action)
      ?.hasUnknownAmount === true
  return (
    <div>
      {scopedSettlement(followUp) && (
        <TextField
          id="settlement-action"
          select
          fullWidth
          margin="normal"
          label={getTranslate('Action verified')}
          value={value.action}
          onChange={(event) =>
            onChange(
              initialSettlement(
                followUp,
                event.target.value as PaymentFollowUpAction
              )
            )
          }
          SelectProps={{ native: true }}
          disabled={disabled}>
          {followUp.outstandingByAction?.map((row) => (
            <option key={row.action} value={row.action}>
              {settlementActionLabel(row.action)}
            </option>
          ))}
        </TextField>
      )}
      <TextField
        id="settlement-outcome"
        select
        fullWidth
        margin="normal"
        label={getTranslate('Verified outcome')}
        value={value.outcome}
        onChange={(event) => update('outcome', event.target.value)}
        SelectProps={{ native: true }}
        inputProps={{ 'aria-label': getTranslate('Verified outcome') }}
        disabled={disabled}>
        {settlementOutcomes(followUp, value.action || undefined).map(
          (outcome) => (
            <option key={outcome} value={outcome}>
              {settlementOutcomeLabel(outcome)}
            </option>
          )
        )}
      </TextField>
      {monetary && (
        <TextField
          fullWidth
          required
          margin="normal"
          id="settlement-amount"
          label={getTranslate('Verified settlement amount')}
          value={value.amount}
          onChange={(event) => update('amount', event.target.value)}
          inputProps={{ inputMode: 'decimal', maxLength: 30 }}
          disabled={disabled}
        />
      )}
      <TextField
        fullWidth
        required
        margin="normal"
        id="settlement-reference"
        label={getTranslate('Verification reference')}
        value={value.reference}
        onChange={(event) => update('reference', event.target.value)}
        inputProps={{ maxLength: 200 }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        required
        margin="normal"
        type="datetime-local"
        id="settlement-verified-at"
        label={getTranslate('Verified at')}
        value={value.verifiedAt}
        onChange={(event) => update('verifiedAt', event.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
      />
      {wallet && (
        <TextField
          fullWidth
          required
          margin="normal"
          id="settlement-payer-acceptance"
          label={getTranslate('Payer acceptance reference')}
          value={value.payerAcceptanceReference}
          onChange={(event) =>
            update('payerAcceptanceReference', event.target.value)
          }
          inputProps={{ maxLength: 200 }}
          disabled={disabled}
        />
      )}
      {credit && (
        <>
          <TextField
            fullWidth
            required
            margin="normal"
            id="settlement-wallet-transaction"
            label={getTranslate('Wallet transaction reference')}
            value={value.walletTransactionReference}
            onChange={(event) =>
              update('walletTransactionReference', event.target.value)
            }
            inputProps={{ maxLength: 200 }}
            disabled={disabled}
          />
          <Typography component="p" variant="caption">
            {getTranslate(
              'Verify an existing wallet credit repair. This does not repay the payer or confirm a wallet release.'
            )}
          </Typography>
        </>
      )}
      {monetary && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={value.fullSettlement}
                onChange={(event) =>
                  update('fullSettlement', event.target.checked)
                }
                disabled={disabled || unknown}
              />
            }
            label={
              scopedSettlement(followUp)
                ? getTranslate(
                    'I verified that this fully settles the selected action. Other actions can remain open.'
                  )
                : getTranslate(
                    'I verified that this fully settles the remaining obligation.'
                  )
            }
          />
          {unknown && (
            <Typography component="p" variant="caption">
              {getTranslate(
                'The total is unknown. Record partial verification, or use a referenced no-further-action review after reconciliation.'
              )}
            </Typography>
          )}
        </>
      )}
      {invalid && (
        <Typography id="settlement-validation-error" role="alert" color="error">
          {scopedSettlement(followUp)
            ? getTranslate(
                'Check the selected action, outcome, amount, verification reference and time. Wallet credit repair also needs a wallet transaction reference.'
              )
            : getTranslate(
                'Check the outcome, amount, verification reference and time. Full settlement must match the remaining balance; wallet settlement also needs payer acceptance.'
              )}
        </Typography>
      )}
    </div>
  )
}
